import React, { useEffect, useState } from 'react'
import { Layout, notification } from 'antd'
import SearchRefinementMobile from './SearchRefinementMobile'
import SearchResultsMobile from './SearchResultsMobile'
import DoctorAvailabilityViewMobile from './DoctorAvailabilityViewMobile'
import { useSearchRedux, useBookingRedux } from '../../hooks/reduxHooks'
import './mobile-styles.css'

const { Content } = Layout

// Mobile search engine component
const MobileSearchEngine = ({ isVirtualVisit = false }) => {
  // Mobile navigation state
  const [currentScreen, setCurrentScreen] = useState('refinement') // 'refinement', 'results', 'availability'
  const [hasSearched, setHasSearched] = useState(false)

  // Redux hooks
  const {
    searchFilters,
    searchResults,
    loading,
    error,
    pagination,
    performSearch,
    changePage,
    updateFilter,
    clearError
  } = useSearchRedux()

  const searchType = searchFilters.searchType || 'general_practitioner'

  const {
    selectedDoctor,
    showAvailabilityView,
    openAvailabilityView,
    closeAvailabilityView,
    bookingSuccess,
    bookingError,
    clearBookingError
  } = useBookingRedux()

  // Handle search with error handling
  const handleSearch = async (newFilters) => {
    try {
      setHasSearched(true)
      await performSearch(newFilters)
      setCurrentScreen('results') // Navigate to results screen
    } catch (err) {
      notification.error({
        message: 'Search Failed',
        description: err.message || 'Unable to perform search. Please try again.',
        duration: 4
      })
    }
  }

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    updateFilter(filterType, value)
  }

  // Handle searchType changes
  const setSearchType = (newSearchType) => {
    updateFilter('searchType', newSearchType)
  }

  // Handle page changes with error handling
  const handlePageChange = async (page) => {
    try {
      await changePage(page)
    } catch (err) {
      notification.error({
        message: 'Page Navigation Failed',
        description: err.message || 'Unable to load page. Please try again.',
        duration: 4
      })
    }
  }

  // Handle showing availability view
  const handleViewAvailability = (doctor) => {
    openAvailabilityView(doctor)
    setCurrentScreen('availability')
  }

  // Handle closing availability view
  const handleCloseAvailability = () => {
    closeAvailabilityView()
    setCurrentScreen('refinement')
  }

  // Handle going back to refinement
  const handleBackToRefinement = () => {
    setCurrentScreen('refinement')
  }

  // Handle going back to results
  const handleBackToResults = () => {
    setCurrentScreen('results')
  }

  // Handle appointment booking success
  const handleBookAppointment = (appointmentData) => {
    // The success notification is now shown in the success screen
    // Just navigate back to refinement screen
    closeAvailabilityView()
    setCurrentScreen('refinement') // Navigate back to refinement screen
  }

  // Handle errors
  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Error',
        description: error,
        duration: 4,
        onClose: clearError
      })
    }
  }, [error, clearError])

  useEffect(() => {
    if (bookingError) {
      notification.error({
        message: 'Booking Error',
        description: bookingError,
        duration: 4,
        onClose: clearBookingError
      })
    }
  }, [bookingError, clearBookingError])

  // Show booking success
  useEffect(() => {
    if (bookingSuccess) {
      notification.success({
        message: 'Booking Confirmed',
        description: 'Your appointment has been successfully booked!',
        duration: 4
      })
    }
  }, [bookingSuccess])

  // Initial search on component mount
  useEffect(() => {
    if (searchResults.length === 0 && !loading) {
      performSearch(searchFilters)
    }
  }, [])

  // Show availability view full screen
  if (currentScreen === 'availability' && selectedDoctor) {
    return (
      <DoctorAvailabilityViewMobile
        doctor={selectedDoctor}
        onClose={handleCloseAvailability}
        onBookAppointment={handleBookAppointment}
        isVirtualVisit={isVirtualVisit}
        searchFilters={searchFilters}
      />
    )
  }

  // Show refinement screen first
  if (currentScreen === 'refinement') {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f9fafb' }}>
        <Content>
          <SearchRefinementMobile
            searchType={searchType}
            setSearchType={setSearchType}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            searchFilters={searchFilters}
            loading={loading}
            isFullScreen={true}
            hasSearched={hasSearched}
            isVirtualVisit={isVirtualVisit}
          />
        </Content>
      </Layout>
    )
  }

  // Show results screen
  if (currentScreen === 'results') {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f9fafb' }}>
        <Content style={{ position: 'relative' }}>
          <SearchResultsMobile
            searchType={searchType}
            results={searchResults}
            loading={loading}
            onViewAvailability={handleViewAvailability}
            onBackToRefinement={handleBackToRefinement}
            error={error}
            totalResults={pagination.totalResults}
            searchFilters={searchFilters}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        </Content>
      </Layout>
    )
  }

  return null
}

export default MobileSearchEngine
